import React, { useState, useEffect } from 'react';
import { 
  Select, 
  Row, 
  Col, 
  Table, 
  DatePicker, 
  message, 
  Button, 
  Modal, 
  Form, 
  Input,
  Checkbox,
  Space,
  Typography,
  Popconfirm
} from 'antd';
import { MailOutlined, SendOutlined, DownloadOutlined } from '@ant-design/icons';
import { sendAssignmentEmail, getClients, getClientDepartments, getEmployeeAssignments, deleteEmployeeAssignment, exportAssignmentsPdf } from '../api';
import moment from 'moment';

const { Option } = Select;
const { MonthPicker } = DatePicker;

const { Text } = Typography;
const { TextArea } = Input;

const CurrentAssignmentsPage = () => {
  const [clients, setClients] = useState([]);
  const [clientDepartments, setClientDepartments] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({});
  const [selectedRows, setSelectedRows] = useState([]);
  const [emailModalVisible, setEmailModalVisible] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailForm] = Form.useForm();
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  // Fetch clients on mount
  useEffect(() => {
    const fetchClients = async () => {
      const res = await getClients();
      if (res.success) setClients(res.data);
    };
    fetchClients();
  }, []);

  // Fetch client departments when client changes
  useEffect(() => {
    const fetchClientDepartments = async () => {
      if (filters.client_id) {
        const res = await getClientDepartments(filters.client_id);
        if (res.success) setClientDepartments(res.data);
      } else {
        setClientDepartments([]);
      }
    };
    fetchClientDepartments();
  }, [filters.client_id]);

  // Fetch assignments when filters change
  useEffect(() => {
    const fetchAssignments = async () => {
      setLoading(true);
      try {
        const res = await getEmployeeAssignments(filters);
        if (res.success) setAssignments(res.data);
      } catch (error) {
        message.error('Failed to fetch assignments');
      } finally {
        setLoading(false);
      }
    };
    fetchAssignments();
  }, [filters]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleSendEmail = async (values) => {
    try {
      setSendingEmail(true);
      const recipients = selectedRows.map(row => row.employee?.email).filter(Boolean);
      
      if (recipients.length === 0) {
        message.warning('No valid email addresses found for selected assignments');
        return;
      }

      const response = await sendAssignmentEmail({
        to: recipients,
        subject: values.subject,
        message: values.message,
        assignment_ids: selectedRows.map(row => row.id)
      });

      if (response.success) {
        message.success('Email sent successfully');
        setEmailModalVisible(false);
        emailForm.resetFields();
      } else {
        throw new Error(response.message || 'Failed to send email');
      }
    } catch (error) {
      console.error('Error sending email:', error);
      message.error(error.message || 'Failed to send email');
    } finally {
      setSendingEmail(false);
    }
  };

  const handleSendIndividualEmail = (record) => {
    setSelectedRows([record]);
    emailForm.setFieldsValue({
      subject: `Assignment Details - ${record.employee?.name || 'Employee'}`,
      message: `Dear ${record.employee?.name || 'Employee'},\n\n` +
        `Assignment Details:\n` +
        `- Client: ${record.client?.name || 'N/A'}\n` +
        `- Department: ${record.clientDepartment?.name || 'N/A'}\n` +
        `- Start Date: ${record.start_date ? moment(record.start_date).format('DD MMM YYYY') : 'N/A'}\n` +
        `- End Date: ${record.end_date ? moment(record.end_date).format('DD MMM YYYY') : 'Ongoing'}\n\n` +
        `Please let us know if you have any questions.\n\n` +
        `Best regards,\nManagement`
    });
    setEmailModalVisible(true);
  };

  const rowSelection = {
    selectedRowKeys: selectedRows.map(row => row.id),
    onChange: (_, selectedRows) => {
      setSelectedRows(selectedRows);
    },
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 60 },
    { 
      title: 'Employee', 
      dataIndex: ['employee', 'name'], 
      key: 'employee_name',
      render: (_, record) => record.employee?.name || 'N/A'
    },
    { 
      title: 'Type', 
      dataIndex: 'employee_type', 
      key: 'employee_type',
      render: (type) => type?.charAt(0).toUpperCase() + type?.slice(1) || 'N/A'
    },
    { 
      title: 'Client', 
      dataIndex: ['client', 'name'], 
      key: 'client_name',
      render: (_, record) => record.client?.name || 'N/A'
    },
    { 
      title: 'Department', 
      dataIndex: ['clientDepartment', 'name'], 
      key: 'department_name',
      render: (_, record) => record.clientDepartment?.name || 'N/A'
    },
    { 
      title: 'Start Date', 
      dataIndex: 'start_date', 
      key: 'start_date',
      render: date => date ? moment(date).format('DD MMM YYYY') : 'N/A'
    },
    { 
      title: 'End Date', 
      dataIndex: 'end_date', 
      key: 'end_date',
      render: date => date ? moment(date).format('DD MMM YYYY') : 'N/A'
    },
    { 
      title: 'Status', 
      dataIndex: 'status', 
      key: 'status',
      render: status => (
        <span style={{
          color: status === 'active' ? '#52c41a' : '#faad14',
          fontWeight: 500
        }}>
          {status?.charAt(0).toUpperCase() + status?.slice(1) || 'N/A'}
        </span>
      )
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 160,
      render: (_, record) => (
        <Space>
          <Button 
            type="link" 
            icon={<MailOutlined />} 
            onClick={(e) => {
              e.stopPropagation();
              handleSendIndividualEmail(record);
            }}
          >
            Email
          </Button>
          <Popconfirm
            title="Unassign this employee from assignment?"
            okText="Unassign"
            cancelText="Cancel"
            onConfirm={async (e) => {
              e && e.stopPropagation && e.stopPropagation();
              const res = await deleteEmployeeAssignment(record.id);
              if (res.success) {
                message.success('Unassigned successfully');
                // Refresh assignments
                const res2 = await getEmployeeAssignments(filters);
                if (res2.success) setAssignments(res2.data);
              } else {
                message.error(res.message || 'Failed to unassign');
              }
            }}
            onClick={e => e.stopPropagation()}
          >
            <Button type="link" danger>Unassign</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const handleDownloadCsv = () => {
    if (!assignments.length) {
      message.info('No assignments to download.');
      return;
    }
    const headers = [
      'ID',
      'Employee',
      'Type',
      'Client',
      'Department',
      'Start Date',
      'End Date',
      'Status'
    ];
    const rows = assignments.map(a => [
      a.id,
      a.employee?.name || '',
      a.employee_type || '',
      a.client?.name || '',
      a.clientDepartment?.name || '',
      a.start_date || '',
      a.end_date || '',
      a.status || ''
    ]);
    const csvContent = [headers, ...rows].map(r => r.map(x => `"${String(x).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'current_assignments.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handleDownloadPdf = async () => {
    setDownloadingPdf(true);
    try {
      const blob = await exportAssignmentsPdf(filters);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'current_assignments.pdf';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      message.error(err.message || 'Failed to download PDF');
    } finally {
      setDownloadingPdf(false);
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h2>Current Assignments</h2>
        <Space>
          <Button 
            type="primary" 
            icon={<SendOutlined />} 
            disabled={selectedRows.length === 0}
            onClick={() => {
              emailForm.setFieldsValue({
                subject: 'Your Assignment Details',
                message: 'Dear Employee,\n\nPlease find your assignment details below.\n\nBest regards,\nManagement'
              });
              setEmailModalVisible(true);
            }}
          >
            Send Email ({selectedRows.length} selected)
          </Button>
          <Button
            icon={<DownloadOutlined />} 
            onClick={handleDownloadCsv}
          >
            Download CSV
          </Button>
          <Button
            icon={<DownloadOutlined />} 
            loading={downloadingPdf}
            onClick={handleDownloadPdf}
          >
            Download PDF
          </Button>
        </Space>
      </div>
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={4}>
          <Select
            allowClear
            placeholder="Client"
            value={filters.client_id}
            onChange={v => handleFilterChange('client_id', v)}
            style={{ width: '100%' }}
          >
            {clients.map(c => (
              <Option key={c.id} value={c.id}>{c.name}</Option>
            ))}
          </Select>
        </Col>
        <Col span={4}>
          <Select
            allowClear
            placeholder="Department"
            value={filters.client_department_id}
            onChange={v => handleFilterChange('client_department_id', v)}
            style={{ width: '100%' }}
            disabled={!filters.client_id}
          >
            {clientDepartments.map(dep => (
              <Option key={dep.id} value={dep.id}>{dep.name}</Option>
            ))}
          </Select>
        </Col>
        <Col span={4}>
          <Select
            allowClear
            placeholder="Type"
            value={filters.employee_type}
            onChange={v => handleFilterChange('employee_type', v)}
            style={{ width: '100%' }}
          >
            <Option value="internal">Internal</Option>
            <Option value="subcontractor">Subcontractor</Option>
          </Select>
        </Col>
        <Col span={4}>
          <Select
            allowClear
            placeholder="Status"
            value={filters.status}
            onChange={v => handleFilterChange('status', v)}
            style={{ width: '100%' }}
          >
            <Option value="active">Active</Option>
            <Option value="completed">Completed</Option>
          </Select>
        </Col>
        <Col span={4}>
          <MonthPicker
            placeholder="Month"
            value={filters.start_date ? moment(filters.start_date) : null}
            onChange={d => handleFilterChange('start_date', d ? d.startOf('month').format('YYYY-MM-DD') : undefined)}
            style={{ width: '100%' }}
          />
        </Col>
      </Row>
      <Table
        rowKey="id"
        columns={columns}
        dataSource={assignments}
        loading={loading}
        pagination={{ pageSize: 10 }}
        bordered
        scroll={{ x: true }}
        rowSelection={{
          type: 'checkbox',
          ...rowSelection,
        }}
        onRow={(record) => ({
          onClick: () => {
            const isSelected = selectedRows.some(row => row.id === record.id);
            if (isSelected) {
              setSelectedRows(selectedRows.filter(row => row.id !== record.id));
            } else {
              setSelectedRows([...selectedRows, record]);
            }
          },
        })}
      />

      <Modal
        title={`Send Email (${selectedRows.length} ${selectedRows.length === 1 ? 'Recipient' : 'Recipients'})`}
        open={emailModalVisible}
        onCancel={() => setEmailModalVisible(false)}
        footer={[
          <Button key="cancel" onClick={() => setEmailModalVisible(false)}>
            Cancel
          </Button>,
          <Button 
            key="send" 
            type="primary" 
            loading={sendingEmail}
            onClick={() => emailForm.submit()}
          >
            Send Email
          </Button>,
        ]}
      >
        <Form
          form={emailForm}
          layout="vertical"
          onFinish={handleSendEmail}
        >
          <Form.Item 
            label="To" 
            style={{ marginBottom: 8 }}
          >
            <Text type="secondary">
              {selectedRows.map(row => row.employee?.email).filter(Boolean).join(', ') || 'No valid email addresses'}
            </Text>
          </Form.Item>
          <Form.Item 
            name="subject" 
            label="Subject"
            rules={[{ required: true, message: 'Please enter a subject' }]}
          >
            <Input placeholder="Email subject" />
          </Form.Item>
          <Form.Item 
            name="message" 
            label="Message"
            rules={[{ required: true, message: 'Please enter a message' }]}
          >
            <TextArea rows={8} placeholder="Enter your message here..." />
          </Form.Item>
          <div style={{ marginBottom: 16 }}>
            <Text type="secondary">
              Recipients: {selectedRows.length} {selectedRows.length === 1 ? 'person' : 'people'}
            </Text>
          </div>
        </Form>
      </Modal>
    </div>
  );
};

export default CurrentAssignmentsPage;
